import { useId, useState, type JSX } from 'react'
import type { Freshness } from '../api'
import type { DashboardTab } from '../DashboardView'
import { ago } from '../format'
import { sourceStatus } from '../freshness'
import { SOURCE_GRAPH, type MapColumn, type MapNode, type SectionTarget } from '../sourceMap/graph'
import { edgeKey, layoutSourceMap, type PlacedNode } from '../sourceMap/layout'
import { traceFrom } from '../sourceMap/trace'

// "Where these numbers come from": source, how it gets here, where you see it.
// The pipeline itself is static config (sourceMap/graph.ts); only the status
// line on each job box is live, read from the freshness rows already loaded.
// No money figures here. Pointing at a box traces its route; a source opens in
// a new tab and a section jumps to its chart.

// Room above the boxes for the three column headings.
const HEAD = 28

const COLUMN_HEADINGS: readonly { column: MapColumn; text: string }[] = [
  { column: 'source', text: 'Where it lives' },
  { column: 'job', text: 'How it gets here' },
  { column: 'section', text: 'Where you see it' },
]

const MAP_LAYOUT = layoutSourceMap(SOURCE_GRAPH)

export function SourceMap({
  freshness,
  currentTab,
  onNavigate,
}: {
  freshness: Freshness[]
  currentTab: DashboardTab
  onNavigate: (target: SectionTarget) => void
}): JSX.Element {
  const [activeId, setActiveId] = useState<string | null>(null)
  const ids = useId()
  const titleId = `${ids}-title`
  const headingId = (column: MapColumn): string => `${ids}-${column}`

  const layout = MAP_LAYOUT
  const trace = activeId === null ? null : traceFrom(SOURCE_GRAPH, activeId)
  // A traced edge is drawn last so it sits on top of the quiet ones it crosses.
  const edges = [...layout.edges].sort(
    (a, b) =>
      Number(trace?.edges.has(edgeKey(a)) ?? false) - Number(trace?.edges.has(edgeKey(b)) ?? false),
  )
  const titleOf = new Map(SOURCE_GRAPH.nodes.map((node) => [node.id, node.title]))
  const relatives = (node: MapNode): string => {
    const forward = node.column !== 'section'
    const names = SOURCE_GRAPH.edges
      .filter((edge) => (forward ? edge.from === node.id : edge.to === node.id))
      .map((edge) => titleOf.get(forward ? edge.to : edge.from) ?? '')
    return `${forward ? 'Feeds' : 'Comes from'}: ${names.join(', ')}`
  }

  return (
    <section className="panel source-map" aria-labelledby={titleId}>
      <h2 id={titleId}>Where these numbers come from</h2>
      <p className="chart-sub">
        Click a source to open it, or a section to jump to it. Point at a box to trace its path.
      </p>
      <div className="source-map-scroll">
        <div
          className={`source-map-canvas${trace === null ? '' : ' is-tracing'}`}
          style={{ width: layout.width, height: layout.height + HEAD }}
        >
          {COLUMN_HEADINGS.map(({ column, text }) => (
            <h3
              key={column}
              id={headingId(column)}
              className="source-map-heading"
              style={{ left: layout.nodes.find((n) => n.column === column)?.box.x ?? 0 }}
            >
              {text}
            </h3>
          ))}
          <svg
            className="source-map-edges"
            aria-hidden="true"
            width={layout.width}
            height={layout.height}
            style={{ top: HEAD }}
          >
            {edges.map((edge) => (
              <path
                key={edgeKey(edge)}
                d={edge.d}
                className={`map-edge${trace?.edges.has(edgeKey(edge)) ? ' is-on' : ''}`}
              />
            ))}
          </svg>
          {COLUMN_HEADINGS.map(({ column }) => (
            <ul key={column} className="source-map-column" aria-labelledby={headingId(column)}>
              {layout.nodes
                .filter((node) => node.column === column)
                .map((node) => (
                  <li
                    key={node.id}
                    className={`map-node${node.output ? ' is-output' : ''}${
                      trace?.nodes.has(node.id) ? ' is-on' : ''
                    }`}
                    style={{
                      left: node.box.x,
                      top: node.box.y + HEAD,
                      width: node.box.w,
                      height: node.box.h,
                    }}
                    onMouseEnter={() => setActiveId(node.id)}
                    onMouseLeave={() => setActiveId(null)}
                    onFocus={() => setActiveId(node.id)}
                    onBlur={() => setActiveId(null)}
                  >
                    <NodeBody
                      node={node}
                      freshness={freshness}
                      currentTab={currentTab}
                      onNavigate={onNavigate}
                      relation={relatives(node)}
                    />
                  </li>
                ))}
            </ul>
          ))}
        </div>
      </div>
    </section>
  )
}

function NodeBody({
  node,
  freshness,
  currentTab,
  onNavigate,
  relation,
}: {
  node: PlacedNode
  freshness: Freshness[]
  currentTab: DashboardTab
  onNavigate: (target: SectionTarget) => void
  relation: string
}): JSX.Element {
  const content = (
    <>
      <span className="map-node-title">
        {node.title}
        {node.link.kind === 'external' && (
          <>
            {' '}
            <span aria-hidden="true">↗</span>
            <span className="visually-hidden"> (opens in a new tab)</span>
          </>
        )}
      </span>
      {node.detail !== '' && <span className="map-node-detail">{node.detail}</span>}
      {node.cadence !== undefined && <span className="map-node-detail">{node.cadence}</span>}
      <JobStatus job={node.job} freshness={freshness} />
      {node.link.kind === 'pending' && <span className="map-node-detail">Link not added yet</span>}
      <span className="visually-hidden">{relation}</span>
    </>
  )
  const link = node.link
  if (link.kind === 'external') {
    return (
      <a className="map-node-body" href={link.url} target="_blank" rel="noopener noreferrer">
        {content}
      </a>
    )
  }
  // On the Data sources tab the job boxes would only link to where you already are.
  if (link.kind === 'section' && !(link.target.tab === currentTab && link.target.anchor === null)) {
    return (
      <button type="button" className="map-node-body" onClick={() => onNavigate(link.target)}>
        {content}
      </button>
    )
  }
  return <div className="map-node-body">{content}</div>
}

// A job with no freshness row (freee HR is not tracked) simply has no status
// line, rather than a made-up one.
function JobStatus({
  job,
  freshness,
}: {
  job: string | undefined
  freshness: Freshness[]
}): JSX.Element | null {
  const row = job === undefined ? undefined : freshness.find((source) => source.job === job)
  if (row === undefined) return null
  const status = sourceStatus(row)
  const when = row.lastSucceededAt === null ? 'never loaded' : `updated ${ago(row.lastSucceededAt)}`
  return (
    <span className={`map-status ${status.tone}`}>
      <span className="dot" aria-hidden="true" />
      {status.text}, {when}
    </span>
  )
}
